import {
	ActivityLogPayload,
	ActivityType,
	type MediaItem,
	StorageActivityLogItem,
	StorageAzaleaReferralTaskParameters,
	StorageDatabaseSchema,
	StorageDepartment,
	StorageInsurance,
	StorageLeadingReachReferralTaskParameters,
	StorageLegacyReferralTaskParameters,
	StoragePatient,
	StoragePrimaryProvider,
	StorageSpecialty,
	StorageTask,
	StorageUser,
	TaskAssignee,
	TaskListFilter,
	TaskStatus,
} from "@repo/api/client";
import {
	ConsentStatus,
	SpokenLanguage,
	JobType,
	AsyncJobParamsMap,
	EHRSystem,
} from "@repo/db/client";
import {
	TandemClient as TandemDatabase,
	QueryBuilder as TandemQueryBuilder,
	Transaction as TandemTransaction,
} from "@repo/tandem";
import { codec } from "@repo/utils/codec";
import { Tagged } from "@repo/utils/typeUtils";
import { parseTaskListFilter } from "../taskListFilter";

export type { StorageDatabaseSchema };

export type UserId = Tagged<"UserId", string>;
export type User = {
	id: UserId;
	name: string;
	email: string;
	imageUrl: string | undefined;
	departmentId: DepartmentId | undefined;
	taskAssigneeFilter: TaskAssignee;
	statusFilter: TaskListFilter;
};

export type ActivityLogItemId = Tagged<"ActivityLogItemId", string>;
export type ActivityLogItem = {
	id: ActivityLogItemId;
	taskId: TaskId;
	createdAt: Date;
	/**
	 * @deprecated Use the content field in `payload` instead.
	 */
	content?: string;
	createdBy: UserId | undefined;
	/**
	 * @deprecated Use the `type` field in `payload` instead.
	 */
	activityType: ActivityType;
	mediaItem: MediaItem | undefined;
	payload: ActivityLogPayload;
};

const activityLogItemCodec = codec.object<
	ActivityLogItem,
	StorageActivityLogItem
>({
	id: codec.tagged("ActivityLogItemId"),
	taskId: codec.tagged("TaskId"),
	createdAt: codec.date,
	content: codec.nullable(codec.string),
	createdBy: codec.nullable(codec.tagged("UserId")),
	activityType: codec.enum<ActivityType>(ActivityType),
	mediaItem: codec.nullable(
		codec.object<MediaItem, MediaItem>({
			mediaUrl: codec.string,
			bucket: codec.string,
		}),
	),
	payload: codec.identity<ActivityLogPayload>(),
});

export type LegacyReferralTaskParameters = {
	type?: "legacy";
	referralDate: string;
	specialty: string | undefined;
	patientName: string;
	patientDob: string;
	diagnosisCodes: string[] | undefined;
	context: string | undefined;
	ehrStatus: string | undefined;
};

export type AzaleaReferralTaskParameters = {
	type: "azalea";
	patientName: string;
	patientDateOfBirth: string | undefined;
	sendingProvider: string;
	referredToDate: string;
	dxPointers: string;
	diagnosisCodes: string[] | undefined;
	context: string | undefined;
	ehrStatus: string | undefined;
};

export type LeadingReachReferralTaskParameters = {
	type: "leadingreach";
	draftReferralId: string;
	outboundReferralId: string | undefined;
	diagnosisCodes: string[] | undefined;
	context: string | undefined;
	ehrStatus: string | undefined;
};

export type ReferralTaskParameters =
	| LegacyReferralTaskParameters
	| AzaleaReferralTaskParameters
	| LeadingReachReferralTaskParameters;

export type LinkedTaskParameters = ReferralTaskParameters;

export type TaskId = Tagged<"TaskId", string>;
export type Task = {
	id: TaskId;
	title: string;
	currentStep: string;
	status: TaskStatus;
	department: string;
	taskType: string | undefined;
	priority: "Urgent" | "High" | "Medium" | undefined;
	linkedTaskParameters: LinkedTaskParameters | undefined;
	patient: PatientId;
	patientName: string;
	unsnoozeDate: Date | undefined;
	activityLog: ActivityLogItemId[];
	createdBy: UserId | undefined;
	assignedTo: UserId | undefined;
	assigneeType: "user" | "agent";
	referralId: ReferralId | undefined;
	specialtyId: SpecialtyId | undefined;
	lastActivityTimestamp: Date;
	runningJobs: Array<
		{
			[K in JobType]: {
				id: string;
				jobType: K;
				startedTime: Date;
				jobParams: AsyncJobParamsMap[K];
				reasoning: string | undefined;
			};
		}[JobType]
	>;
	createdAt: Date;
};

const legacyReferralTaskParametersCodec = codec.custom<
	LegacyReferralTaskParameters,
	StorageLegacyReferralTaskParameters
>("legacyReferralTaskParameters", {
	encode: (input: unknown) => {
		const i = input as LegacyReferralTaskParameters;
		return {
			type: i.type,
			referralDate: i.referralDate,
			specialty: i.specialty,
			patientName: i.patientName,
			patientDob: i.patientDob,
			diagnosisCodes: i.diagnosisCodes ?? null,
			context: i.context ?? null,
			ehrStatus: i.ehrStatus ?? null,
		};
	},
	decode: (input: unknown) => {
		const i = input as StorageLegacyReferralTaskParameters;
		return {
			type: i.type,
			referralDate: i.referralDate,
			specialty: i.specialty,
			patientName: i.patientName,
			patientDob: i.patientDob,
			diagnosisCodes: i.diagnosisCodes ?? undefined,
			context: i.context ?? undefined,
			ehrStatus: i.ehrStatus ?? undefined,
		};
	},
});

const azaleaReferralTaskParametersCodec = codec.custom<
	AzaleaReferralTaskParameters,
	StorageAzaleaReferralTaskParameters
>("azaleaReferralTaskParameters", {
	encode: (input: unknown) => {
		const i = input as AzaleaReferralTaskParameters;
		return {
			type: "azalea" as const,
			patientName: i.patientName,
			patientDateOfBirth: i.patientDateOfBirth ?? null,
			sendingProvider: i.sendingProvider,
			referredToDate: i.referredToDate,
			dxPointers: i.dxPointers,
			diagnosisCodes: i.diagnosisCodes ?? null,
			context: i.context ?? null,
			ehrStatus: i.ehrStatus ?? null,
		};
	},
	decode: (input: unknown) => {
		const i = input as StorageAzaleaReferralTaskParameters;
		return {
			type: "azalea" as const,
			patientName: i.patientName,
			patientDateOfBirth: i.patientDateOfBirth ?? undefined,
			sendingProvider: i.sendingProvider,
			referredToDate: i.referredToDate,
			dxPointers: i.dxPointers,
			diagnosisCodes: i.diagnosisCodes ?? undefined,
			context: i.context ?? undefined,
			ehrStatus: i.ehrStatus ?? undefined,
		};
	},
});

const leadingReachReferralTaskParametersCodec = codec.custom<
	LeadingReachReferralTaskParameters,
	StorageLeadingReachReferralTaskParameters
>("leadingReachReferralTaskParameters", {
	encode: (input: unknown) => {
		const i = input as LeadingReachReferralTaskParameters;
		return {
			type: "leadingreach" as const,
			draftReferralId: i.draftReferralId,
			outboundReferralId: i.outboundReferralId ?? null,
			diagnosisCodes: i.diagnosisCodes ?? null,
			context: i.context ?? null,
			ehrStatus: i.ehrStatus ?? null,
		};
	},
	decode: (input: unknown) => {
		const i = input as StorageLeadingReachReferralTaskParameters;
		return {
			type: "leadingreach" as const,
			draftReferralId: i.draftReferralId,
			outboundReferralId: i.outboundReferralId ?? undefined,
			diagnosisCodes: i.diagnosisCodes ?? undefined,
			context: i.context ?? undefined,
			ehrStatus: i.ehrStatus ?? undefined,
		};
	},
});

const referralTaskParametersCodec = codec.oneOf(
	azaleaReferralTaskParametersCodec,
	leadingReachReferralTaskParametersCodec,
	legacyReferralTaskParametersCodec,
);

const taskCodec = codec.object<Task, StorageTask>({
	id: codec.tagged("TaskId"),
	title: codec.string,
	currentStep: codec.string,
	status: codec.enum(TaskStatus),
	department: codec.string,
	taskType: codec.nullable(codec.string),
	priority: codec.nullable(codec.literal("Urgent", "High", "Medium")),
	linkedTaskParameters: codec.nullable(referralTaskParametersCodec),
	patient: codec.tagged("PatientId"),
	patientName: codec.string,
	unsnoozeDate: codec.nullable(codec.date),
	activityLog: codec.nullable(
		codec.array(codec.tagged("ActivityLogItemId")),
		[],
	),
	createdBy: codec.nullable(codec.tagged("UserId")),
	assignedTo: codec.nullable(codec.tagged("UserId")),
	assigneeType: codec.literal("user", "agent"),
	referralId: codec.nullable(codec.tagged("ReferralId")),
	specialtyId: codec.nullable(codec.tagged("SpecialtyId")),
	lastActivityTimestamp: codec.date,
	runningJobs: codec.array(
		codec.custom<
			{
				[K in JobType]: {
					id: string;
					jobType: K;
					startedTime: Date;
					jobParams: AsyncJobParamsMap[K];
					reasoning: string | undefined;
				};
			}[JobType],
			any
		>("runningJob", {
			encode: (input: any) => ({
				id: input.id,
				jobType: input.jobType,
				startedTime: input.startedTime.toISOString(),
				jobParams: input.jobParams,
				reasoning: input.reasoning,
			}),
			decode: (input: any) =>
				({
					id: input.id,
					jobType: input.jobType as JobType,
					startedTime: new Date(input.startedTime),
					jobParams: input.jobParams as AsyncJobParamsMap[JobType],
					reasoning: input.reasoning,
				}) as {
					[K in JobType]: {
						id: string;
						jobType: K;
						startedTime: Date;
						jobParams: AsyncJobParamsMap[K];
						reasoning: string | undefined;
					};
				}[JobType],
		}),
	),
	createdAt: codec.date,
});

export type Insurance = {
	id: string;
	planName: string | undefined;
	memberId: string;
	type: string | undefined;
	phone: string | undefined;
	address: string | undefined;
	payor: string | undefined;
	isPrimary: boolean | undefined;
	isExpired: boolean | undefined;
	specialistRefsReqPriorAuth: boolean | undefined;
	patientInsuranceId: string | undefined;
	imageUrls: string[] | undefined;
};

export type PrimaryProvider = {
	id: string;
	name: string;
	specialty: string | undefined;
	faxNumber: string | undefined;
	phoneNumber: string | undefined;
	npi: string | undefined;
};

export type PatientId = Tagged<"PatientId", string>;
export type Patient = {
	id: PatientId;
	name: string;
	phoneNumber: string | undefined;
	responsiblePartyName: string | undefined;
	dateOfBirth: Date | undefined;
	address: string | undefined;
	primaryLanguage: SpokenLanguage | undefined;
	insurance: Insurance[];
	primaryProvider: PrimaryProvider | undefined;
	consentRequestStatus: ConsentStatus;
	pcpValidatedAt: Date | undefined;
	spruceContactId: string | undefined;
};

export type ReferralId = Tagged<"ReferralId", string>;

const insuranceCodec = codec.object<Insurance, StorageInsurance>({
	id: codec.string,
	planName: codec.nullable(codec.string),
	memberId: codec.string,
	type: codec.nullable(codec.string),
	phone: codec.nullable(codec.string),
	address: codec.nullable(codec.string),
	payor: codec.nullable(codec.string),
	isPrimary: codec.nullable(codec.boolean),
	isExpired: codec.nullable(codec.boolean),
	specialistRefsReqPriorAuth: codec.nullable(codec.boolean),
	patientInsuranceId: codec.nullable(codec.string),
	imageUrls: codec.nullable(codec.array(codec.string)),
});

const primaryProviderCodec = codec.object<
	PrimaryProvider,
	StoragePrimaryProvider
>({
	id: codec.string,
	name: codec.string,
	specialty: codec.nullable(codec.string),
	faxNumber: codec.nullable(codec.string),
	phoneNumber: codec.nullable(codec.string),
	npi: codec.nullable(codec.string),
});

const patientCodec = codec.object<Patient, StoragePatient>({
	id: codec.tagged("PatientId"),
	name: codec.string,
	phoneNumber: codec.nullable(codec.string),
	responsiblePartyName: codec.nullable(codec.string),
	dateOfBirth: codec.nullable(codec.date),
	address: codec.nullable(codec.string),
	primaryLanguage: codec.nullable(codec.enum(SpokenLanguage)),
	insurance: codec.array(insuranceCodec),
	primaryProvider: codec.nullable(primaryProviderCodec),
	consentRequestStatus: codec.enum(ConsentStatus),
	pcpValidatedAt: codec.nullable(codec.date),
	spruceContactId: codec.nullable(codec.string),
});

export type DepartmentId = Tagged<"DepartmentId", string>;
export type Department = {
	id: DepartmentId;
	name: string;
	externalFacingName: string | undefined;
	tenantEhrConnectionId: string | undefined;
	ehrVendorName: EHRSystem | undefined;
	pullJobStatus: "running" | "finished" | "error" | "timed_out" | undefined;
	pullJobFinishedTime: Date | undefined;
	pullJobErrorMessage: string | undefined;
	defaultLocationPreferences:
		| Array<{
				city: string;
				state: string;
		  }>
		| undefined;
	allowedOrganizationIds: string[] | undefined;
	createdAt: Date;
	updatedAt: Date;
};

export type SpecialtyId = Tagged<"SpecialtyId", string>;
export type Specialty = {
	id: SpecialtyId;
	name: string;
	aliases: string | undefined;
	source: string | undefined;
	createdAt: Date;
	updatedAt: Date;
};

export type DatabaseSchema = {
	task: Task;
	patient: Patient;
	activityLogItem: ActivityLogItem;
	user: User;
	department: Department;
	specialty: Specialty;
};

// Falls back rather than throwing: every user row in the tenant is broadcast
// to every client, so one person's unrecognised filter would otherwise kill a
// colleague's sync pull. See parseTaskListFilter.
const statusFilterCodec = codec.custom<TaskListFilter, TaskListFilter>(
	"taskListFilter",
	{ encode: (input) => input, decode: parseTaskListFilter },
);

const userCodec = codec.object<User, StorageUser>({
	id: codec.tagged("UserId"),
	name: codec.string,
	email: codec.string,
	imageUrl: codec.nullable(codec.string),
	departmentId: codec.nullable(codec.tagged("DepartmentId")),
	taskAssigneeFilter: codec.enum(TaskAssignee),
	statusFilter: statusFilterCodec,
});

type LocationPreference = { city: string; state: string };

const departmentCodec = codec.object<Department, StorageDepartment>({
	id: codec.tagged("DepartmentId"),
	name: codec.string,
	externalFacingName: codec.nullable(codec.string),
	tenantEhrConnectionId: codec.nullable(codec.string),
	ehrVendorName: codec.nullable(codec.enum(EHRSystem)),
	pullJobStatus: codec.nullable(
		codec.literal("running", "finished", "error", "timed_out"),
	),
	pullJobFinishedTime: codec.nullable(codec.date),
	pullJobErrorMessage: codec.nullable(codec.string),
	defaultLocationPreferences: codec.nullable(
		codec.array(
			codec.object<LocationPreference, LocationPreference>({
				city: codec.string,
				state: codec.string,
			}),
		),
	),
	allowedOrganizationIds: codec.nullable(codec.array(codec.string)),
	createdAt: codec.date,
	updatedAt: codec.date,
});

const specialtyCodec = codec.object<Specialty, StorageSpecialty>({
	id: codec.tagged("SpecialtyId"),
	name: codec.string,
	aliases: codec.nullable(codec.string),
	source: codec.nullable(codec.string),
	createdAt: codec.date,
	updatedAt: codec.date,
});

export const schemaCodecs = {
	task: taskCodec,
	patient: patientCodec,
	activityLogItem: activityLogItemCodec,
	user: userCodec,
	department: departmentCodec,
	specialty: specialtyCodec,
};

export type Database = TandemDatabase<DatabaseSchema>;
export type Transaction = TandemTransaction<DatabaseSchema>;
export type QueryBuilder<Collection extends keyof DatabaseSchema> =
	TandemQueryBuilder<DatabaseSchema, Collection>;
